import { CustomSelect } from '@/components/form/CustomSelect';
import { TutorsMultiSelect } from '@/components/form/TutorsMultiSelect';
import { CustomDatePicker } from '@/components/form/CustomDatePicker';
import { formatPhoneInput } from '@/lib/phoneMask';
import { STATUS_LABEL, TARIFF_LABEL, TYPE_LABEL, autoValidityForStatus } from '@/lib/clientFormat';
import type { ClientDetail, ClientEditOptions } from '@/api/clients';
import type { GeneralEditState } from './generalEditState';

/**
 * Редактирование "Общее" — поля и их доступность 1:1 из Orchid
 * (CustomerEditLayout/CustomerAdminLayout/CustomerPersonalDataLayout/
 * CustomerTutorLayout, см. докблок CustomerController::update() в Laravel):
 * mt1_login и email на редактировании навсегда disabled(!isCreate) — здесь,
 * на странице карточки клиента, всегда только просмотр (в отличие от
 * мастера создания, ClientCreatePage.tsx, где оба поля вводятся один раз).
 * Обязательность полей — тоже 1:1 Orchid (только визуально: address/ogrn
 * required, когда статус не "demo"), сервер реально блокирует именно эти
 * два плюс name — см. validateCustomerRequest()/CustomerController::update().
 */
export function GeneralEditForm({
  data,
  state,
  onChange,
  editOptions,
  error,
  fieldErrors,
  saving,
}: {
  data: ClientDetail;
  state: GeneralEditState;
  onChange: (state: GeneralEditState) => void;
  editOptions: ClientEditOptions | null;
  error: string | null;
  fieldErrors: string[];
  saving: boolean;
}) {
  const set = <K extends keyof GeneralEditState>(key: K, value: GeneralEditState[K]) => {
    onChange({ ...state, [key]: value });
  };
  const personalDataRequired = state.status !== 'demo';
  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  return (
    <div className={`detail__grid-2${saving ? ' detail__grid-2--saving' : ''}`} aria-busy={saving}>
      {error && (
        <p className="field-error" style={{ gridColumn: '1 / -1' }}>
          {error}
        </p>
      )}

      <div className="card">
        <h2 className="detail__subtitle">Информация о компании</h2>
        <div className="edit-fields">
          <label className={`edit-field${invalid('name')}`}>
            <span>Наименование компании *</span>
            <input
              type="text"
              value={state.name}
              required
              onChange={(e) => set('name', e.target.value)}
            />
          </label>

          <label className="edit-field edit-field--readonly">
            <span>Наименование кабинета в Maintest 5i *</span>
            <input type="text" value={data.mt1Name || '–'} disabled readOnly />
          </label>

          <label className="edit-field edit-field--checkbox edit-field--full">
            <input
              type="checkbox"
              checked={state.isMt1HallAllowed}
              onChange={(e) => set('isMt1HallAllowed', e.target.checked)}
            />
            <span>Разрешить вход в Maintest-5i через персональный холл</span>
          </label>

          <label className={`edit-field edit-field--full${invalid('tutorIds')}`}>
            <span>Кураторы *</span>
            <TutorsMultiSelect
              tutorIds={state.tutorIds}
              tutors={editOptions?.tutors}
              disabled={!editOptions}
              onChange={(ids) => set('tutorIds', ids)}
            />
          </label>

          <label className="edit-field">
            <span>Тип *</span>
            <CustomSelect
              tooltip="Тип"
              value={state.type}
              options={Object.entries(TYPE_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(value) => set('type', value)}
            />
          </label>

          <label className="edit-field">
            <span>Тип тарификации *</span>
            <CustomSelect
              tooltip="Тип тарификации"
              value={state.tariffType}
              options={Object.entries(TARIFF_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(value) => set('tariffType', value)}
            />
          </label>

          <label className="edit-field">
            <span>Статус аккаунта *</span>
            <CustomSelect
              tooltip="Статус аккаунта"
              value={state.status}
              options={Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label }))}
              onChange={(status) => {
                // 1:1 асинхронный предпросмотр Orchid (asyncStatusChange) —
                // здесь сразу на клиенте, поле validity остаётся редактируемым дальше.
                onChange({ ...state, status, validity: autoValidityForStatus(status) });
              }}
            />
          </label>

          <label className="edit-field">
            <span>Дата отключения кабинета</span>
            <CustomDatePicker
              value={state.validity}
              placeholder="Бессрочно"
              onChange={(value) => set('validity', value)}
            />
            <span className="edit-field__hint">Оставьте пустым для неограниченного срока действия</span>
          </label>
        </div>

        <div className="detail__divider" />
        <h2 className="detail__subtitle detail__subtitle--sm">Заметки</h2>
        <textarea
          className="edit-field__textarea"
          rows={3}
          value={state.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>

      <div className="stack">
        <div className="card">
          <h2 className="detail__subtitle">Основной контакт</h2>
          <div className="edit-fields">
            <label className="edit-field">
              <span>Фамилия</span>
              <input type="text" value={state.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </label>
            <label className="edit-field">
              <span>Имя</span>
              <input
                type="text"
                value={state.firstName}
                onChange={(e) => set('firstName', e.target.value)}
              />
            </label>
            <label className="edit-field">
              <span>Отчество</span>
              <input
                type="text"
                value={state.middleName}
                onChange={(e) => set('middleName', e.target.value)}
              />
            </label>
            <label className="edit-field">
              <span>Должность</span>
              <input
                type="text"
                value={state.position}
                onChange={(e) => set('position', e.target.value)}
              />
            </label>
            <label className="edit-field edit-field--readonly">
              <span>Электронная почта *</span>
              <input type="email" value={data.adminContact.email || '–'} disabled readOnly />
            </label>
            <label className="edit-field">
              <span>Телефон</span>
              <input
                type="tel"
                placeholder="+7 (___) ___-__-__"
                value={state.phone}
                onChange={(e) => set('phone', formatPhoneInput(e.target.value))}
              />
            </label>
          </div>
        </div>

        <div className="card">
          <h2 className="detail__subtitle">Персональные данные</h2>
          <div className="edit-fields edit-fields--stacked">
            <label className="edit-field">
              <span>Шаблон соглашения{personalDataRequired && ' *'}</span>
              <CustomSelect
                tooltip="Шаблон соглашения"
                disabled={!editOptions}
                value={state.operatorTemplateId}
                options={[
                  { value: '', label: 'По умолчанию' },
                  ...(editOptions?.operatorTemplates.map((t) => ({ value: String(t.id), label: t.name })) ?? []),
                ]}
                onChange={(value) => set('operatorTemplateId', value)}
              />
            </label>
            <label className={`edit-field${invalid('ogrn')}`}>
              <span>ОГРН{personalDataRequired && ' *'}</span>
              <input
                type="text"
                required={personalDataRequired}
                value={state.ogrn}
                onChange={(e) => set('ogrn', e.target.value)}
              />
            </label>
            <label className={`edit-field${invalid('address')}`}>
              <span>Юридический адрес{personalDataRequired && ' *'}</span>
              <input
                type="text"
                required={personalDataRequired}
                value={state.address}
                onChange={(e) => set('address', e.target.value)}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
